import { InfoOutlined } from '@mui/icons-material';
import Launch from '@mui/icons-material/Launch';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import React from 'react';
import { Link } from 'react-router-dom';
import { HomeDashData, homeStyles } from '..';
import { AuthContext } from '../../../contexts/auth-context';
import { AgtStaffPermissions } from '../../../types';
import { toCurrency } from '../../../utils/formatter';
import { AgentRace } from './agent-race';
import { BadgeLevel } from './badge-level';
import { ContestTracker } from './contest-tracker';
import { CRMTotals } from './crm-totals';
import { MediaCalls } from './media-calls';
import { TicketTotals } from './ticket-totals';

export function Today(props: {
  AgtNo: string;
  data: HomeDashData;
  isMobile: boolean;
  loading: boolean;
  newAgent?: boolean;
}) {
  // Context
  const {
    state: { session },
  } = React.useContext(AuthContext);
  // Props
  const { AgtNo, isMobile, data, loading } = props;

  // Set featured youtube short source
  const videoSRC = data?.EmbedURL || '';

  // Check if user is agent staff account and get their permissions
  const staffAgtNo = session?.getAccessToken().decodePayload()['myStaffForAgt'];
  const staffPerms =
    session?.getAccessToken().decodePayload()['cognito:groups'] || [];
  const cantView =
    staffAgtNo && !staffPerms.includes(AgtStaffPermissions['Commissions']);

  return (
    <Paper sx={{ ...homeStyles.section, position: 'relative' }}>
      {props.loading ? (
        <CircularProgress sx={{ position: 'absolute', top: 10, left: 10 }} />
      ) : null}
      <Box py={3} sx={{ ...homeStyles.title }}>
        Alliance Resource Center
      </Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        px={{ xs: 0, sm: 2 }}
        pb={{ xs: 0, sm: 3 }}
        spacing={3}
        alignItems="stretch"
        justifyContent="space-evenly"
      >
        <Box sx={{ flex: 1 }}>
          {props.newAgent ? (
            <AgentRace />
          ) : (
            <Stack
              flex={1}
              direction="column"
              alignItems="center"
              justifyContent="center"
              p={0}
              spacing={2}
            >
              <Box
                width="100%"
                p={1}
                sx={{
                  ...homeStyles.box,
                  minHeight: '120px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  justifyContent: 'space-between',
                }}
              >
                <Box
                  sx={{
                    letterSpacing: 1.1,
                    p: { xs: 0, sm: 1 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textTransform: 'uppercase',
                  }}
                >
                  {data?.IncomeTitle ||
                    new Date().getFullYear() + ' Income Projection'}

                  {data?.IncomeTooltip && (
                    <Tooltip
                      title={data?.IncomeTooltip || ''}
                      arrow
                      placement="bottom"
                      slotProps={{
                        tooltip: { sx: { backgroundColor: '#17191B', p: 1 } },
                        arrow: { sx: { color: '#17191B' } },
                      }}
                    >
                      <InfoOutlined
                        fontSize="small"
                        sx={{
                          transform: 'scale(.8)',
                          cursor: 'help',
                          color: '#fff',
                        }}
                      />
                    </Tooltip>
                  )}

                  <Tooltip
                    title="Go to Commissions"
                    arrow
                    placement="top"
                    slotProps={{
                      tooltip: { sx: { backgroundColor: '#17191B', p: 1 } },
                      arrow: { sx: { color: '#17191B' } },
                    }}
                  >
                    <Link
                      to="/business/commissions"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Launch
                        fontSize="small"
                        sx={{ transform: 'scale(.8)' }}
                      />
                    </Link>
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    pb: 1,
                    color: '#64E8AB',
                    fontSize: 36,
                    fontWeight: 700,
                    letterSpacing: { xs: 1, sm: 5 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: { xs: 85, sm: null },
                    filter: cantView ? 'blur(8px)' : '',
                    pointerEvents: cantView ? 'none' : 'auto',
                    userSelect: cantView ? 'none' : 'auto',
                  }}
                >
                  {toCurrency({
                    value: data?.IncomeValue || 0,
                  })}{' '}
                </Box>
              </Box>
              <Stack
                width="100%"
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems="stretch"
              >
                <CRMTotals AgtNo={AgtNo} />
                <Stack
                  direction={{ xs: 'column', sm: 'row', md: 'column' }}
                  width="100%"
                  flex={1}
                  spacing={2}
                >
                  <TicketTotals data={data} loading={loading} />
                  <BadgeLevel
                    data={data}
                    loading={loading}
                    isMobile={isMobile}
                  />
                </Stack>
              </Stack>
            </Stack>
          )}
          <Box sx={{ textAlign: 'center', pt: 1 }}>
            <Box
              component={Link}
              to="/team/agency-progress-report"
              sx={{
                p: 1,
                px: 2,
                textDecorationLine: 'none',
                ...homeStyles.button,
                textTransform: 'uppercase',
                color: '#000',
                textAlign: 'center',
                cursor: 'pointer',
                border: 'none',
                height: 28,
              }}
            >
              Agency Progress Report
            </Box>
          </Box>
        </Box>

        <Stack
          spacing={{ xs: 0, md: 2 }}
          flex={1}
          direction="column"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          {isMobile ? null : (
            <Box flex={1} width="100%" height="100%">
              <MediaCalls
                videoSRC={videoSRC}
                isPlaylist={data?.IsPlaylist || false}
              />
            </Box>
          )}
          <ContestTracker AgtNo={AgtNo} isMobile={isMobile} />
        </Stack>
      </Stack>
    </Paper>
  );
}
